import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Package, Mail, Landmark, MessageCircle, CreditCard, X } from 'lucide-react';
import DisclaimerBox from '../components/DisclaimerBox';
import { getOrder, getSettings, customerOrders, getCustomerToken } from '../lib/api';

const COMMUNITY_URL = 'https://chat.whatsapp.com/CZSeIvAtdy8H7hCLTWHdHI';
const COMMUNITY_IMG = 'https://customer-assets-jai6qajn.emergentagent.net/job_alp-luxury-store/artifacts/mvyog4aq_6A1AF3DC-3CE6-4EE2-9184-1557EE4CF620.png';
const NUDGE_KEY = 'alp_community_nudge_v1';

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNudge, setShowNudge] = useState(false);

  useEffect(() => {
    const num = params.get('order');
    getSettings().then(setSettings).catch(() => {});
    if (!num) { setLoading(false); return; }
    getOrder(num).then(setOrder).catch(() => setOrder(null)).finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    if (!order || localStorage.getItem(NUDGE_KEY)) return;
    let timer;
    const arm = () => { timer = setTimeout(() => setShowNudge(true), 1400); };
    if (getCustomerToken()) {
      customerOrders().then((list) => { if ((list || []).length <= 1) arm(); else localStorage.setItem(NUDGE_KEY, 'skipped'); }).catch(arm);
    } else {
      arm();
    }
    return () => clearTimeout(timer);
  }, [order]);

  const dismissNudge = (joined) => {
    localStorage.setItem(NUDGE_KEY, joined ? 'joined' : 'dismissed');
    setShowNudge(false);
  };

  if (loading) return <div className="max-w-3xl mx-auto px-5 py-32 text-center text-alp-gray">Loading&hellip;</div>;

  if (!order) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <h1 className="font-serif text-4xl text-alp-cream mb-4">No recent order found</h1>
        <Link to="/shop" className="text-alp-gold">Browse Catalog</Link>
      </div>
    );
  }

  const subtotal = order.subtotal ?? order.items.reduce((a, i) => a + i.price * i.qty, 0);
  const wa = settings?.whatsappNumber ? settings.whatsappNumber.replace(/[^0-9]/g, '') : '';
  const waMsg = encodeURIComponent(
    `Hi, I've placed order ${order.orderNumber} for $${order.total.toFixed(2)} AUD and have sent my bank transfer. Here is my payment confirmation:`
  );
  const waLink = wa ? `https://wa.me/${wa}?text=${waMsg}` : null;

  return (
    <div className="bg-alp-black min-h-screen">
      {showNudge && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="community-nudge-modal">
          <div className="w-full max-w-md border border-alp-gold/40 rounded-xl bg-alp-dark p-7 text-center relative shadow-[0_0_50px_rgba(212,175,55,0.15)]">
            <button onClick={() => dismissNudge(false)} data-testid="community-nudge-close" aria-label="Close"
              className="absolute top-3 right-3 text-alp-gray hover:text-alp-gold transition-colors"><X size={16} /></button>
            <img src={COMMUNITY_IMG} alt="ALP Community" className="w-20 h-20 rounded-full object-cover mx-auto mb-4 shadow-[0_0_25px_rgba(212,175,55,0.3)]" />
            <p className="text-alp-gold text-[10px] tracking-[0.3em] uppercase mb-2">One more thing…</p>
            <h2 className="font-serif text-2xl text-alp-cream mb-2">Join Our Community</h2>
            <p className="text-alp-gray text-sm leading-relaxed mb-5">
              You're one of us now — hop into our private WhatsApp community for restock alerts, member-only offers and direct access to the ALP team.
            </p>
            <a href={COMMUNITY_URL} target="_blank" rel="noopener noreferrer" onClick={() => dismissNudge(true)} data-testid="community-nudge-join"
              className="btn-glow w-full inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors rounded">
              <MessageCircle size={15} /> Join the Community
            </a>
            <button onClick={() => dismissNudge(false)} data-testid="community-nudge-later"
              className="mt-3 text-alp-gray text-xs underline underline-offset-4 hover:text-alp-gold transition-colors">Maybe later</button>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-20">
        <div className="text-center mb-12">
          <CheckCircle2 size={56} strokeWidth={1} className="text-alp-gold mx-auto mb-6" />
          <h1 className="font-serif text-5xl text-alp-cream mb-3">Order Received</h1>
          <p className="text-alp-gray">Your order has been received but payment is still pending. Please complete payment using the instructions below. Once payment is confirmed, your order status will be updated and you will receive confirmation.</p>
          <p className="text-alp-gold text-lg tracking-[0.15em] mt-4" data-testid="confirm-order-number">{order.orderNumber}</p>
        </div>

        <div className="border border-alp-line rounded p-6 bg-alp-card/40 mb-6">
          <h2 className="font-serif text-2xl text-alp-cream mb-5">Order Summary</h2>
          <div className="space-y-3 mb-5">
            {order.items.map((i) => (
              <div key={i.id} className="flex justify-between text-sm">
                <span className="text-alp-cream/80">{i.name} <span className="text-alp-gray">({i.size}) &times;{i.qty}</span></span>
                <span className="text-alp-cream">${(i.price * i.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="gold-divider my-4" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-alp-gray"><span>Subtotal</span><span className="text-alp-cream">${subtotal.toFixed(2)}</span></div>
            {order.discountCode ? <div className="flex justify-between text-alp-gold"><span>Discount ({order.discountCode})</span><span>&minus;${Number(order.discountAmount || 0).toFixed(2)}</span></div> : null}
            <div className="flex justify-between text-alp-gray"><span>Express Post</span><span className="text-alp-cream">{order.shipping === 0 ? 'FREE' : `$${order.shipping.toFixed(2)}`}</span></div>
            <div className="flex justify-between text-lg pt-1"><span className="text-alp-cream">Total Owing</span><span className="text-alp-gold" data-testid="confirm-total">${order.total.toFixed(2)} AUD</span></div>
          </div>
        </div>

        {/* Payment instructions */}
        {order.paymentMethod === 'Card' || order.paymentMethod === 'Afterpay' ? (
          <div className="border border-alp-gold/50 rounded p-6 bg-alp-card/40 mb-6" data-testid="card-instructions">
            <div className="flex items-center gap-2 mb-4"><CreditCard size={20} className="text-alp-gold" /><h2 className="font-serif text-2xl text-alp-cream">{order.paymentMethod === 'Afterpay' ? 'Afterpay Payment' : 'Card Payment'}</h2></div>
            <div className="space-y-1.5 text-sm">
              <Row label="Amount" value={`$${order.total.toFixed(2)} AUD`} highlight />
              {Number(order.cardSurcharge || 0) > 0 ? <Row label="Includes card fee" value={`$${Number(order.cardSurcharge).toFixed(2)}`} /> : null}
            </div>
            <div className="mt-4 rounded border border-alp-gold/40 bg-alp-gold/5 px-4 py-3" data-testid="card-order-note">
              <p className="text-sm text-alp-cream/90 leading-relaxed">Important: on the payment page, enter the <span className="text-alp-gold font-semibold">amount ${order.total.toFixed(2)}</span> and your order number <span className="text-alp-gold font-semibold tracking-wider">{order.orderNumber}</span> in the description so we can match your payment.</p>
            </div>
            {settings?.cardPaymentLink ? (
              <a href={settings.cardPaymentLink} target="_blank" rel="noopener noreferrer" data-testid="card-pay-btn" className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 mt-4 text-xs tracking-[0.2em] uppercase font-semibold bg-alp-gold text-alp-black hover:bg-alp-goldlight transition-colors rounded">
                <CreditCard size={16} /> Pay ${order.total.toFixed(2)} {order.paymentMethod === 'Afterpay' ? 'with Afterpay' : 'by Card'}
              </a>
            ) : null}
            <p className="text-sm text-alp-cream/90 mt-3">Your order will be confirmed and dispatched once payment is received.</p>
          </div>
        ) : (
        <div className="border border-alp-gold/50 rounded p-6 bg-alp-card/40 mb-6" data-testid="bank-instructions">
          <div className="flex items-center gap-2 mb-4"><Landmark size={20} className="text-alp-gold" /><h2 className="font-serif text-2xl text-alp-cream">Bank Transfer Payment</h2></div>
          <div className="space-y-1.5 text-sm">
            <Row label="Account Name" value={settings?.bankAccountName} />
            <Row label="BSB" value={settings?.bankBSB} />
            <Row label="Account Number" value={settings?.bankAccountNumber} />
            <Row label="Reference" value={order.orderNumber} highlight />
            <Row label="Amount" value={`$${order.total.toFixed(2)} AUD`} highlight />
          </div>
          <p className="text-xs text-alp-gray/80 mt-4">{settings?.bankReferenceText || 'Use your order number as the payment reference.'}</p>
          {settings?.bankNameNote ? (
            <div className="mt-4 rounded border border-alp-gold/30 bg-alp-gold/5 px-4 py-3" data-testid="bank-name-note">
              <p className="text-sm text-alp-cream/90 leading-relaxed">{settings.bankNameNote}</p>
            </div>
          ) : null}
          <p className="text-sm text-alp-cream/90 mt-3">Your order will be confirmed and dispatched once payment is received.</p>
        </div>
        )}

        {/* WhatsApp confirmation */}
        {waLink && (
          <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-confirm-btn" className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 mb-8 text-xs tracking-[0.2em] uppercase font-semibold bg-emerald-600 text-white hover:bg-emerald-500 transition-colors rounded">
            <MessageCircle size={16} /> Send Payment Confirmation via WhatsApp
          </a>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="hairline rounded p-5 flex gap-3 items-start">
            <Mail size={20} className="text-alp-gold shrink-0 mt-0.5" />
            <p className="text-sm text-alp-gray">A confirmation email with these details has been sent to <span className="text-alp-cream">{order.customer.email}</span>.</p>
          </div>
          <div className="hairline rounded p-5 flex gap-3 items-start">
            <Package size={20} className="text-alp-gold shrink-0 mt-0.5" />
            <p className="text-sm text-alp-gray">Your order ships from Australian stock &mdash; dispatched promptly once payment is confirmed.</p>
          </div>
        </div>

        <DisclaimerBox compact />

        <div className="text-center mt-10">
          <Link to="/shop" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">Continue Shopping</Link>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex justify-between">
      <span className="text-alp-gray">{label}</span>
      <span className={highlight ? 'text-alp-gold font-semibold' : 'text-alp-cream'}>{value || '\u2014'}</span>
    </div>
  );
}
